import React, { useState } from 'react';
import {
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import { formatDate } from '../../Formatters';
import { Task } from '../../interfaces';
import { Delete, Check, Edit, Visibility, FilterList } from '@mui/icons-material';
import ConfirmDialog from '../../components/ConfirmDialog';
import EditDialog from './EditDialog';
import DetailsDialog from './DetailsDialog';
import { Link } from 'react-router-dom';
import { Counter } from '../../components/Counter';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

interface Props {
  tasks: Task[];
  deleteTask: (task: Task) => void;
  updateTask: (any) => void;
  licenseId?: number;
  descriptionWidth?: number;
}

export default function TaskTable({ tasks, licenseId, descriptionWidth, updateTask, deleteTask }: Props) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState<Task | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  const handleDeleteClicked = (task: Task): void => {
    setCurrentTask(task);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = (): void => {
    setCurrentTask(undefined);
    setConfirmDialogOpen(false);
  };

  const doDeleteTask = (): void => {
    if (currentTask === undefined) return;
    deleteTask(currentTask);
    closeConfirmDialog();
  };

  const handleEditClicked = (task: Task): void => {
    setCurrentTask(task);
    setEditDialogOpen(true);
  };

  const closeEditDialog = (): void => {
    setCurrentTask(undefined);
    setEditDialogOpen(false);
  };

  const handleDetailsClicked = (task: Task): void => {
    setCurrentTask(task);
    setDetailsDialogOpen(true);
  };

  const closeDetailsDialog = (): void => {
    setCurrentTask(undefined);
    setDetailsDialogOpen(false);
  };

  const doUpdateTask = (isComplete: boolean, comment?: string): void => {
    if (currentTask === undefined) return;
    updateTask({ variables: { input: { id: currentTask.id, isComplete, comment } } });
    closeEditDialog();
  };

  const stringSort = (a: string, b: string) => {
    if (!a && b) {
      return -1;
    }
    if (a && !b) {
      return 1;
    }
    if (!a && !b) {
      return 0;
    }

    return b.localeCompare(a);
  };

  if (tasks.length === 0) {
    return <Typography>Ingen oppgaver</Typography>;
  }

  const filteredTasks = tasks.filter((x) => !typeFilter.some((f) => f === x.typeName));
  const rows = filteredTasks.map((task) => ({
    id: task.id,
    task,
  }));

  const columns: GridColDef<(typeof rows)[number]>[] = [
    ...(!licenseId
      ? [
        {
          field: 'licenseName',
          headerName: 'Kunde',
          minWidth: 220,
          flex: 1,
          valueGetter: (_, row) => row.task.licenseName,
          renderCell: (params) => <LicenseName id={params.row.task.bksLicenseId} companyName={params.row.task.licenseName} />,
        } as GridColDef<(typeof rows)[number]>,
      ]
      : []),
    {
      field: 'typeName',
      headerName: 'Type',
      minWidth: 160,
      flex: 1,
      valueGetter: (_, row) => row.task.typeName,
      renderHeader: () => (
        <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
          <span>Type</span>
          <ColumnFilter
            values={Array.from(new Set(tasks.map((x) => x.typeName).sort(stringSort)))}
            selectedValues={typeFilter}
            setSelectedValues={setTypeFilter}
          />
        </Stack>
      ),
    },
    {
      field: 'description',
      headerName: 'Beskrivelse',
      minWidth: 260,
      flex: 2,
      valueGetter: (_, row) => row.task.description,
      renderCell: (params) => (
        <div style={{ maxWidth: descriptionWidth, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{params.row.task.description}</div>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Opprettet',
      minWidth: 150,
      flex: 1,
      valueGetter: (_, row) => row.task.createdAt,
      renderCell: (params) => <span>{formatDate(params.row.task.createdAt)}</span>,
    },
    {
      field: 'lastUpdatedByName',
      headerName: 'Sist endret av',
      minWidth: 200,
      flex: 1,
      valueGetter: (_, row) => row.task.lastUpdatedByName || row.task.createdByName,
    },
    {
      field: 'completedAt',
      headerName: 'Fullført',
      minWidth: 110,
      flex: 1,
      valueGetter: (_, row) => row.task.completedAt,
      renderCell: (params) => <span title={params.row.task.comment}>{params.row.task.completedAt ? <Check style={{ color: 'green' }} /> : null}</span>,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      minWidth: 130,
      flex: 1,
      renderCell: (params) => (
        <>
          {!params.row.task.completedAt && (
            <IconButton title="Redigér oppgave" onClick={() => handleEditClicked(params.row.task)} size="small">
              <Edit />
            </IconButton>
          )}
          {params.row.task.completedAt && (
            <IconButton title="Vis detaljer" onClick={() => handleDetailsClicked(params.row.task)} size="small">
              <Visibility />
            </IconButton>
          )}
          {params.row.task.isCreatedManually && (
            <IconButton title="Slett" onClick={() => handleDeleteClicked(params.row.task)} size="small">
              <Delete />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Counter list={filteredTasks} />
      <DataGridPro
        autoHeight
        density="compact"
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        pagination
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 50,
              page: 0,
            },
          },
        }}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
        }}
      />
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Slette oppgave"
        message="Vil du slette oppgaven?"
        confirmText="Slett"
        denyText="Avbryt"
        confirm={() => doDeleteTask()}
        deny={() => closeConfirmDialog()}
        DialogProps={{ maxWidth: 'xs', fullWidth: true }}
      />
      {currentTask && !currentTask.completedAt && (
        <EditDialog
          open={editDialogOpen}
          task={currentTask}
          onCancel={() => closeEditDialog()}
          onSubmit={(isComplete, comment) => doUpdateTask(isComplete, comment)}
        />
      )}
      {currentTask && currentTask.completedAt && <DetailsDialog open={detailsDialogOpen} task={currentTask} onClose={() => closeDetailsDialog()} />}
    </>
  );
}

function LicenseName({ id, companyName }: { id: number; companyName: string }) {
  return <Link to={'/license/' + id}>{companyName ? companyName : '<Mangler navn>'}</Link>;
}

function ColumnFilter({
  values,
  selectedValues,
  setSelectedValues,
}: {
  values: string[];
  selectedValues: string[];
  setSelectedValues(values: string[]): void;
}) {
  const [open, setOpen] = useState(false);
  const [workingTypeFilters, setWorkingTypeFilters] = useState<string[]>(selectedValues);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setOpen(true);
    e.stopPropagation();
  };
  const handleClose = () => {
    setOpen(false);
    setWorkingTypeFilters(selectedValues);
  };
  const handleOk = () => {
    setSelectedValues(workingTypeFilters);
    handleClose();
  };

  const handleFilterTypeChange = (type: string) => {
    if (workingTypeFilters.some((x) => x === type)) {
      setWorkingTypeFilters(workingTypeFilters.filter((x) => x !== type));
    } else {
      setWorkingTypeFilters([type, ...workingTypeFilters]);
    }
  };

  const handleAllChange = () => {
    if (workingTypeFilters.length === 0) {
      setWorkingTypeFilters(values);
    } else {
      setWorkingTypeFilters([]);
    }
  };

  const filterActive = selectedValues.length > 0;

  return (
    <>
      <IconButton onClick={handleOpen} title={filterActive ? 'Filtrer liste' : 'Listen er filtrert'} size="large">
        <FilterList color={filterActive ? 'error' : 'inherit'} />
      </IconButton>
      <Dialog open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Endre filter</DialogTitle>
        <DialogContent>
          <form>
            <FormControl component="fieldset">
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={workingTypeFilters.length === 0} onChange={handleAllChange} value="Alle" />}
                  label="Alle"
                />
                <hr />
                {values.map((t) => (
                  <FormControlLabel
                    key={t}
                    control={<Checkbox checked={!workingTypeFilters.some((x) => x === t)} onChange={() => handleFilterTypeChange(t)} value={t} />}
                    label={t}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Avbryt</Button>
          <Button onClick={handleOk} color="primary">
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
